import { TrendingUp } from 'lucide-react';
import { EvolucionMensualBalance } from '../services/balanceService';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

function getBalanceColorClass(balance: number): string {
  return balance >= 0 ? 'text-emerald-700' : 'text-red-700';
}

function formatCLP(amount: number): string {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
  }).format(amount);
}

interface EvolucionAnualTableProps {
  readonly datos: EvolucionMensualBalance[];
  readonly loading: boolean;
  readonly año: number;
  readonly mesSeleccionado?: number;
  readonly onMesClick?: (mes: number) => void;
}

export const EvolucionAnualTable: React.FC<EvolucionAnualTableProps> = ({
  datos,
  loading,
  año,
  mesSeleccionado,
  onMesClick,
}) => {
  const ahora = new Date();
  const añoActual = ahora.getFullYear();
  const mesActual = ahora.getMonth() + 1;

  const esMesFuturo = (mes: number) =>
    año === añoActual && mes > mesActual;

  const mesesConDatos = datos.filter(d => !esMesFuturo(d.mes));
  const totalIngresos = mesesConDatos.reduce((s, d) => s + d.ingresos, 0);
  const totalEgresos = mesesConDatos.reduce((s, d) => s + d.egresos, 0);
  // El financiamiento de deudas es dinero prestado: columna aparte, solo si algún mes lo tuvo; suma al balance.
  const totalFinanciamiento = mesesConDatos.reduce((s, d) => s + d.financiamiento, 0);
  const hayFinanciamiento = mesesConDatos.some(d => d.financiamiento > 0);
  const totalBalance = totalIngresos + totalFinanciamiento - totalEgresos;

  const getRowBg = (d: EvolucionMensualBalance, esFuturo: boolean): string => {
    if (esFuturo) return 'bg-slate-50';
    if (mesSeleccionado === d.mes) return 'bg-blue-50';
    if (d.balance > 0) return 'bg-emerald-50';
    if (d.balance < 0) return 'bg-red-50';
    return 'bg-slate-50';
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-violet-100 p-2 rounded-lg">
          <TrendingUp className="w-6 h-6 text-violet-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-800">Evolución Anual {año}</h3>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left py-3 px-2 sm:px-4 font-semibold text-slate-600">Mes</th>
                <th className="text-right py-3 px-2 sm:px-4 font-semibold text-slate-600">Ingresos</th>
                <th className="text-right py-3 px-2 sm:px-4 font-semibold text-slate-600">Egresos</th>
                {hayFinanciamiento && (
                  <th className="text-right py-3 px-2 sm:px-4 font-semibold text-slate-600">Financiamiento</th>
                )}
                <th className="text-right py-3 px-2 sm:px-4 font-semibold text-slate-600">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {datos.map((d) => {
                const futuro = esMesFuturo(d.mes);
                const seleccionado = mesSeleccionado === d.mes;
                const esMesActual = año === añoActual && d.mes === mesActual;
                const clickeable = !futuro && !!onMesClick;

                const balanceColorClass = futuro ? 'text-slate-400' : getBalanceColorClass(d.balance);

                return (
                  <tr
                    key={d.mes}
                    className={`transition-colors ${getRowBg(d, futuro)} ${
                      seleccionado ? 'border-l-4 border-blue-500' : ''
                    } ${clickeable ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                    {...(clickeable
                      ? {
                          role: 'button',
                          tabIndex: 0,
                          onClick: () => onMesClick(d.mes),
                          onKeyDown: (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') onMesClick(d.mes);
                          },
                        }
                      : {})}
                  >
                    <td className="py-3 px-2 sm:px-4 font-medium text-slate-700">
                      {MESES[d.mes - 1]}
                      {esMesActual && (
                        <span className="ml-1 sm:ml-2 text-xs font-semibold text-blue-600 bg-blue-100 px-1.5 sm:px-2 py-0.5 rounded-full">
                          actual
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-2 sm:px-4 text-right text-emerald-700 font-medium">
                      {futuro ? '--' : formatCLP(d.ingresos)}
                    </td>
                    <td className="py-3 px-2 sm:px-4 text-right text-red-700 font-medium">
                      {futuro ? '--' : formatCLP(d.egresos)}
                    </td>
                    {hayFinanciamiento && (
                      <td className="py-3 px-2 sm:px-4 text-right text-teal-700 font-medium">
                        {futuro ? '--' : formatCLP(d.financiamiento)}
                      </td>
                    )}
                    <td className={`py-3 px-2 sm:px-4 text-right font-bold ${balanceColorClass}`}>
                      {futuro ? '--' : formatCLP(d.balance)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 border-t-2 border-slate-300 font-bold">
                <td className="py-3 px-2 sm:px-4 text-slate-700">Total</td>
                <td className="py-3 px-2 sm:px-4 text-right text-emerald-700">{formatCLP(totalIngresos)}</td>
                <td className="py-3 px-2 sm:px-4 text-right text-red-700">{formatCLP(totalEgresos)}</td>
                {hayFinanciamiento && (
                  <td className="py-3 px-2 sm:px-4 text-right text-teal-700">{formatCLP(totalFinanciamiento)}</td>
                )}
                <td className={`py-3 px-2 sm:px-4 text-right ${totalBalance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {formatCLP(totalBalance)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
